<template>
  <o-dialog
    ref="dialog"
    :title="$t('components.customerNote.edit.title')"
    :info="$t('components.customerNote.edit.info')"
    :options="[
      { text: $t('general.cancel'), type: 'cancel' },
      { text: $t('components.customerNote.edit.save'), type: 'confirm', callback: confirm },
    ]"
    :portal="portal"
    :valid="validate"
  >
    <div v-if="customerNote" class="mt-4 text-left ">
      <o-form ref="form">
        <o-field-group v-slot="{ status }" :label="$t('note.type')" :name="$t('note.type')" rules="required" class="block pb-3">
          <segment-field v-model="customerNote.type" :options="customerNoteTypeOptions" :status="status" />
        </o-field-group>

        <o-field-group v-slot="{ status }" :label="$t('general.note')" :name="$t('general.note')" rules="required" class="block pb-3">
          <text-area-field v-model="customerNote.content" rows="6" :status="status" />
        </o-field-group>
      </o-form>
    </div>
  </o-dialog>
</template>

<script setup lang="ts">
import { SegmentField, TextAreaField } from '@/components/fields'

import { CustomerNoteType, CustomerNoteTypeLabels } from '~/_api/models/customerNote'

import type { Customer } from '~/_api/models/customer'

interface Props {
  customer: Customer
  portal?: string
  loading?: boolean
}

interface Emits {
  (e: 'success'): void
}

const props = withDefaults(defineProps<Props>(), {
  portal: undefined,
  loading: false,
})

const emit = defineEmits<Emits>()
const { $api, $i18n, $notification, $bus } = useNuxtApp()

const dialog = ref<any>()
const form = ref<any>()
const customerNote = ref<any>(null)

const customerNoteTypeOptions = computed(() => {
  return Object.values(CustomerNoteType)
    .filter((value): value is CustomerNoteType => typeof value === 'number')
    .map((value) => {
      return {
        label: $i18n.t(CustomerNoteTypeLabels[value]),
        value,
      }
    })
})

const isCustomerNoteTypeNote = computed(() => {
  return customerNote.value?.type === CustomerNoteType.note
})

const isCustomerNoteTypeForumla = computed(() => {
  return customerNote.value?.type === CustomerNoteType.formula
})

const isCustomerNoteTypeAlert = computed(() => {
  return customerNote.value?.type === CustomerNoteType.alert
})

const open = (note: any) => {
  customerNote.value = note.copy()
  dialog.value.open()
}

const confirm = async () => {
  try {
    const response = await $api.customerNote.save(customerNote.value)
    const result = response.data

    if (result) {
      emit('success')

      $bus.emit.updated('CustomerNote', result)
      $bus.emit.updated('Customer', { idCustomer: Number(result.idCustomer) })

      $notification.toast({
        title: $i18n.t('components.customerNote.edit.success'),
        text: $i18n.t('components.customerNote.edit.successInfo'),
        variant: 'success',
      })

      return true
    }
  } catch (e) {
    console.log(e)
  }
}

const validate = () => {
  return form.value.validate()
}

defineExpose({ open })
</script>
